import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, X, Calendar, MapPin } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

interface Assignment {
    id: string;
    clientName: string;
    startDate: string;
    task: string;
    status: string;
    city?: string;
}

interface NotificationToastProps {
    assignments: Assignment[];
    onDismiss: (id: string) => void;
}

export const NotificationToast: React.FC<NotificationToastProps> = ({ assignments, onDismiss }) => {
    return (
        <div className="fixed top-4 right-4 z-50 space-y-2 max-w-sm">
            <AnimatePresence>
                {assignments.map((assignment) => (
                    <motion.div
                        key={assignment.id}
                        initial={{ opacity: 0, x: 100, scale: 0.8 }}
                        animate={{ opacity: 1, x: 0, scale: 1 }}
                        exit={{ opacity: 0, x: 100, scale: 0.8 }}
                        className="glass-panel p-4 border-l-4 border-accent shadow-xl"
                    >
                        <div className="flex items-start gap-3">
                            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center">
                                <Bell className="text-accent" size={20} />
                            </div>

                            <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2 mb-1">
                                    <h4 className="font-semibold text-white text-sm">
                                        Nouvelle intervention
                                    </h4>
                                    <button
                                        onClick={() => onDismiss(assignment.id)}
                                        className="text-gray-400 hover:text-white transition-colors"
                                    >
                                        <X size={16} />
                                    </button>
                                </div>

                                <p className="text-gray-300 text-sm font-medium mb-2">
                                    {assignment.clientName}
                                </p>

                                <p className="text-gray-400 text-xs mb-2 line-clamp-2">
                                    {assignment.task}
                                </p>

                                <div className="flex items-center gap-3 text-xs text-gray-500">
                                    <div className="flex items-center gap-1">
                                        <Calendar size={12} />
                                        <span>
                                            {format(parseISO(assignment.startDate), 'dd MMM yyyy', { locale: fr })}
                                        </span>
                                    </div>

                                    {assignment.city && (
                                        <div className="flex items-center gap-1">
                                            <MapPin size={12} />
                                            <span>{assignment.city}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </motion.div>
                ))}
            </AnimatePresence>
        </div>
    );
};
